import React, { useEffect, useState } from 'react';
import { Grid, makeStyles, TextField, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from 'store';
import { departmentActions } from 'actions/departmentActions';
import { ValidatedTextInput } from 'components/ValidatedTextInput';
import 'translations/translations';
import { useParams } from 'react-router-dom';
import { UserAutocomplete } from 'features/common/UserAutocomplete';
import { UserModelDto } from '.generated/api';

interface ParamTypes {
  orgId: string;
}

type StepProps = {
  stepTitleString: string;
  buttonText: string;
};

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
    textAlign: 'right',
    fontSize: '0.8rem',
  },
}));

const AddAdminToDepartmentStep: React.FC<StepProps> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);
  const classes = useStyles();
  const { orgId } = useParams<ParamTypes>();
  const [inputs, setInputs] = useState({
    contactName: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [emailError, setEmailError] = useState(false);

  const departmentId = useSelector(
    (state: ApplicationState) => state.department?.id,
  );

  const departmentError = useSelector(
    (state: ApplicationState) => state.department?.error,
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleChangeName = (name: string) => {
    setInputs((inputs) => ({ ...inputs, contactName: name }));
  };

  const handleEmailChange = (name: string, value: string, error: boolean) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
    setEmailError(error);
  };

  const setExistingUser = (user: UserModelDto) => {
    setInputs((inputs) => ({
      ...inputs,
      contactName: user.name,
      contactEmail: user.userEmail,
      contactPhone: user.phone,
    }));
  };

  useEffect(() => {
    handleButtonState(
      inputs.contactName,
      inputs.contactEmail,
      inputs.contactPhone,
      emailError,
    );
  }, [inputs, emailError]);

  const handleButtonState = (
    contactName: string,
    contactEmail: string,
    contactPhone: string,
    error?: boolean,
  ): void => {
    if (error) {
      dispatch({ type: 'DISABLE_BUTTON', disabled: true });
      return;
    }

    if ((contactName !== '' || contactPhone !== '') && contactEmail === '') {
      dispatch({ type: 'DISABLE_BUTTON', disabled: true });
      return;
    }

    dispatch({ type: 'DISABLE_BUTTON', disabled: false });
  };

  const callbackFn = () => {
    if (inputs.contactEmail !== '') {
      dispatch(
        departmentActions.addAdminToDept(
          orgId,
          departmentId,
          inputs.contactEmail,
          inputs.contactName,
          inputs.contactPhone,
        ),
      );
    } else {
      dispatch({ type: 'SET_FLOW_COMPLETED' });
    }
  };

  useEffect(() => {
    dispatch({ type: 'SET_CALLBACK', callbackFn });
  });

  return (
    <>
      <Grid item xs={12}>
        <UserAutocomplete
          onSelectOption={setExistingUser}
          onChangeName={handleChangeName}
          label={t('organizations_page.create_org.add_user_step.contact_name')}
          value={inputs.contactName}
        />
        <ValidatedTextInput
          type="email"
          required={false}
          label={t('organizations_page.create_org.add_user_step.contact_email')}
          handleChangeCallback={handleEmailChange}
          inputName="contactEmail"
          autoFocus={false}
          defaultValue={inputs.contactEmail}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          variant="outlined"
          margin="none"
          fullWidth
          id="contactPhone"
          label={t('organizations_page.create_org.add_user_step.contact_phone')}
          name="contactPhone"
          autoFocus={false}
          onChange={handleChange}
          value={inputs.contactPhone}
        ></TextField>
      </Grid>
      <Typography className={classes.error}>
        {!!departmentError && departmentError}
      </Typography>
    </>
  );
};

export { AddAdminToDepartmentStep };
